import { forwardRef } from 'react';
import type { MouseEvent } from 'react';
import { flushSync } from 'react-dom';
import { Link, useLocation, useNavigate, type LinkProps } from 'react-router-dom';

/**
 * Every route is prerendered to <path>/index.html, which Cloudflare Pages serves
 * at /pricing/ and 308s /pricing to. An internal link without the slash points
 * crawlers at a redirect, and Search Console lists /pricing as a separate,
 * unindexed URL. So in-app links carry the trailing slash, matching the
 * canonical and the sitemap (vite-plugins/prerender.ts).
 */
export function withSlash(to: string): string {
  const split = to.search(/[?#]/);
  const path = split < 0 ? to : to.slice(0, split);
  const rest = split < 0 ? '' : to.slice(split);
  if (!path.startsWith('/') || path.endsWith('/') || /\.[a-z0-9]+$/i.test(path)) return to;
  return `${path}/${rest}`;
}

type StartViewTransition = (update: () => void) => unknown;

/**
 * react-router's Link, with string paths normalised by withSlash, and route
 * changes run inside a View Transition where the browser has one: the old page
 * fades out, the new one rises in, the header holds still (statio.css). A plain
 * left click to another path only; modified clicks, same-path clicks and
 * browsers without the API get the ordinary instant navigation.
 */
export const SiteLink = forwardRef<HTMLAnchorElement, LinkProps>(function SiteLink({ to, onClick, ...rest }, ref) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const target = typeof to === 'string' ? withSlash(to) : to;

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    const start = (document as Document & { startViewTransition?: StartViewTransition }).startViewTransition;
    if (e.defaultPrevented || !start || typeof target !== 'string') return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || rest.target === '_blank') return;
    const nextPath = target.split(/[?#]/)[0];
    if (nextPath === pathname || nextPath === withSlash(pathname)) return;
    e.preventDefault();
    start.call(document, () => flushSync(() => navigate(target)));
  };

  return <Link ref={ref} to={target} onClick={handleClick} {...rest} />;
});
